/**
 * A weather field as a coloured wash under the chart.
 *
 * This is the thing PredictWind and XC Weather both do that we were not: the
 * COLOUR carries the value, continuously, across every pixel, and the symbol on
 * top carries only the direction. Colouring the symbols instead (which is where
 * we got to) means the strength is only readable where a symbol happens to be,
 * and the gradient between them, which is what actually tells you where the
 * front is, is invisible.
 *
 * It also unlocks the greyed-out half of the picker. Rain, pressure, gusts and
 * air temperature are not arrows: they are a value per point. Once a value per
 * point can be drawn, they all can, and the only thing that changes between them
 * is the colour ramp. One mechanism, every variable.
 *
 * HOW: bilinear interpolation from the model's coarse grid into an offscreen
 * canvas, which MapLibre takes as an `image` source pinned to the field's corners.
 * No tile server, no new dependency, and it re-renders in a few milliseconds when
 * the hour changes.
 *
 * The interpolation is honest about what it is: the model has one value every few
 * kilometres and everything between is smoothing, not data. That is true of every
 * weather map ever printed, but it is worth writing down next to the code that
 * does it.
 */

/** A colour stop: at or above `value`, this colour. */
export interface RampStop {
  value: number;
  /** [r, g, b], 0-255. */
  rgb: [number, number, number];
}

/** How solid the wash is, and what counts as nothing worth colouring. */
export interface FieldRenderOptions {
  /** 0-255. The field test says about 100: at 150 the chart drowned (fb-msie7xvq-1). */
  alpha?: number;
  /** Values under this draw as clear. Rain's "no rain" case. */
  hideBelow?: number;
}

/** The corners MapLibre needs for an image source, clockwise from top-left. */
export type ImageCorners = [
  [number, number],
  [number, number],
  [number, number],
  [number, number],
];

/** Values on a regular lon/lat grid, row-major from the SOUTH-WEST corner. */
export interface FieldGrid {
  cols: number;
  rows: number;
  /** [w, s, e, n]. */
  bounds: [number, number, number, number];
  /** cols*rows values, row 0 being the southernmost. NaN where not forecast. */
  values: number[];
}

/** Linear blend between two colours. */
function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

/**
 * The colour for a value on a ramp, blended between stops.
 *
 * Blended rather than stepped, unlike the arrow colours. On a symbol a hard band
 * gives you a category to read; across a whole field it would draw contour edges
 * that look like weather fronts and are not. The legend still prints the
 * boundaries, so a number can still be read off it.
 */
export function rampColour(stops: RampStop[], value: number): [number, number, number] {
  if (!stops.length) return [0, 0, 0];
  if (!Number.isFinite(value)) return stops[0].rgb;
  if (value <= stops[0].value) return stops[0].rgb;
  const last = stops[stops.length - 1];
  if (value >= last.value) return last.rgb;
  for (let i = 1; i < stops.length; i++) {
    if (value <= stops[i].value) {
      const lo = stops[i - 1];
      const hi = stops[i];
      const span = hi.value - lo.value;
      return mix(lo.rgb, hi.rgb, span === 0 ? 0 : (value - lo.value) / span);
    }
  }
  return last.rgb;
}

/**
 * Bilinear sample of the grid at fractional cell coordinates.
 *
 * Returns NaN when any corner is missing, so a hole in the model's coverage stays
 * a hole rather than being filled in from its neighbours. Inventing sea state or
 * wind for water the model does not cover is exactly the failure this app has
 * been fixing all day.
 */
export function sampleGrid(grid: FieldGrid, fx: number, fy: number): number {
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(grid.cols - 1, x0 + 1);
  const y1 = Math.min(grid.rows - 1, y0 + 1);
  if (x0 < 0 || y0 < 0 || x0 >= grid.cols || y0 >= grid.rows) return NaN;
  const tx = fx - x0;
  const ty = fy - y0;
  const at = (x: number, y: number) => grid.values[y * grid.cols + x];
  const v00 = at(x0, y0);
  const v10 = at(x1, y0);
  const v01 = at(x0, y1);
  const v11 = at(x1, y1);
  if (!Number.isFinite(v00) || !Number.isFinite(v10) || !Number.isFinite(v01) || !Number.isFinite(v11)) {
    return NaN;
  }
  const top = v00 + (v10 - v00) * tx;
  const bot = v01 + (v11 - v01) * tx;
  return top + (bot - top) * ty;
}

/**
 * Render the grid into RGBA pixels.
 *
 * Kept separate from any canvas so it can be tested without one: jsdom has no 2D
 * context, and the interpolation and the colouring are the parts worth pinning.
 * Pixel row 0 is the NORTH edge, because that is how an image is laid out and how
 * MapLibre will place it, while grid row 0 is the south. Getting that flip wrong
 * puts the weather upside down, which is why there is a test for it.
 */
export function renderFieldPixels(
  grid: FieldGrid,
  stops: RampStop[],
  width: number,
  height: number,
  opts: FieldRenderOptions = {},
): Uint8ClampedArray {
  const alpha = opts.alpha ?? 190;
  const hideBelow = opts.hideBelow ?? -Infinity;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let py = 0; py < height; py++) {
    // Flip: image top is north, grid top index is north-most row (rows-1).
    const fy = ((height - 1 - py) / Math.max(1, height - 1)) * (grid.rows - 1);
    for (let px = 0; px < width; px++) {
      const fx = (px / Math.max(1, width - 1)) * (grid.cols - 1);
      const v = sampleGrid(grid, fx, fy);
      const i = (py * width + px) * 4;
      // Two different kinds of nothing, both drawn as nothing. NaN is "the model
      // does not cover this water"; below hideBelow is "it does cover it, and the
      // answer is none", which is the normal case for rain and would otherwise
      // wash the whole chart blue on a dry day.
      if (!Number.isFinite(v) || v < hideBelow) {
        out[i + 3] = 0;
        continue;
      }
      const [r, g, b] = rampColour(stops, v);
      out[i] = r;
      out[i + 1] = g;
      out[i + 2] = b;
      out[i + 3] = alpha;
    }
  }
  return out;
}

/** The image corners for a field, clockwise from top-left, as MapLibre wants. */
export function cornersFor(bounds: [number, number, number, number]): ImageCorners {
  const [w, s, e, n] = bounds;
  return [
    [w, n],
    [e, n],
    [e, s],
    [w, s],
  ];
}

/**
 * Paint the pixels into a canvas and hand back a data URL.
 *
 * Returns null anywhere there is no canvas (tests, SSR), so a caller can simply
 * skip the wash rather than guarding every call site.
 */
export function fieldImageUrl(
  grid: FieldGrid,
  stops: RampStop[],
  width = 256,
  height = 256,
  opts: FieldRenderOptions = {},
): string | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const img = ctx.createImageData(width, height);
    img.data.set(renderFieldPixels(grid, stops, width, height, opts));
    ctx.putImageData(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
